import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import useLoader from 'hooks/useLoader';
import {
    changeRunStatus,
    changeTaskStatus,
    getActivity,
    getRun,
    saveTask
} from 'data/repositories/MigrationRunbook';
import { MigrationRun, RunbookActivity, RunbookTask } from 'data/schemas/MigrationRunbook';
import useRunbookIdentity, { actor } from './useRunbookIdentity';
const date = (value?: string) =>
    value ? new Date(value).toLocaleString('en-GB', { timeZone: 'Asia/Bangkok' }) : '—';
const elapsed = (start?: string, end?: string) => {
    if (!start) return '—';
    const minutes = Math.max(
        0,
        Math.floor(
            ((end ? new Date(end) : new Date()).getTime() - new Date(start).getTime()) / 60000
        )
    );
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};
const statusColor = (status: string) =>
    status === 'blocked'
        ? 'bg-red-100 text-red-800'
        : status === 'completed'
          ? 'bg-green-100 text-green-800'
          : status === 'in_progress'
            ? 'bg-blue-100 text-blue-800'
            : status === 'skipped'
              ? 'bg-gray-200 text-gray-700'
              : 'bg-amber-100 text-amber-800';
interface PresenceViewer {
    citizenId: string;
    displayName: string;
    focusRegionId?: string;
    focusTaskId?: string;
    lastSeenAt: string;
}
interface RemoteCursor {
    citizenId: string;
    displayName: string;
    regionId: string;
    x: number;
    y: number;
    receivedAt: number;
}
const palette = ['#2563eb', '#dc2626', '#059669', '#d97706', '#0891b2', '#db2777'];
const viewerColor = (citizenId: string) => {
    const hash = citizenId.split('').reduce((value, char) => value + char.charCodeAt(0), 0);
    return palette[hash % palette.length];
};
const initials = (name: string) =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join('') || '?';
const runbookAccessToken = () => {
    const stored = localStorage.getItem('MM_APPLICATION') || '';
    try {
        return JSON.parse(stored)?.access_token || '';
    } catch {
        return stored;
    }
};
function CursorLayer({
    rootRef,
    cursors
}: {
    rootRef: RefObject<HTMLDivElement>;
    cursors: RemoteCursor[];
}) {
    const root = rootRef.current;
    if (!root) return null;
    const rootRect = root.getBoundingClientRect();
    return (
        <div className="pointer-events-none absolute inset-0 z-50 overflow-hidden">
            {cursors.map(cursor => {
                const region = Array.from(
                    root.querySelectorAll<HTMLElement>('[data-runbook-region]')
                ).find(item => item.dataset.runbookRegion === cursor.regionId);
                if (!region) return null;
                const rect = region.getBoundingClientRect();
                const left = rect.left - rootRect.left + rect.width * cursor.x;
                const top = rect.top - rootRect.top + rect.height * cursor.y;
                const color = viewerColor(cursor.citizenId);
                return (
                    <div
                        key={cursor.citizenId}
                        className="absolute transition-transform duration-75"
                        style={{ transform: `translate(${left}px, ${top}px)`, color }}
                    >
                        <svg width="22" height="26" viewBox="0 0 22 26" fill="none">
                            <path
                                d="M2 2L19 14H11L7 23L2 2Z"
                                fill={color}
                                stroke="white"
                                strokeWidth="2"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <span
                            className="ml-3 -mt-1 block max-w-[150px] truncate rounded px-2 py-1 text-xs font-semibold text-white shadow"
                            style={{ backgroundColor: color }}
                        >
                            {cursor.displayName}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}
export default function MigrationRunbookLive() {
    const { runId } = useParams<{ runId: string }>();
    const identity = useRunbookIdentity();
    const loader = useLoader();
    const history = useHistory();
    const [run, setRun] = useState<MigrationRun>();
    const [activity, setActivity] = useState<RunbookActivity[]>([]);
    const [tab, setTab] = useState<'execution' | 'rollback'>('execution');
    const [connected, setConnected] = useState(false);
    const [viewers, setViewers] = useState<PresenceViewer[]>([]);
    const [remoteCursors, setRemoteCursors] = useState<Record<string, RemoteCursor>>({});
    const connectionRef = useRef<HubConnection | null>(null);
    const liveRootRef = useRef<HTMLDivElement>(null);
    const lastCursorSentAt = useRef(0);
    const lastFocusRegion = useRef('');
    const load = async (silent = false) => {
        if (!identity.citizenId) return;
        try {
            if (!silent) loader.show();
            const next = await getRun(runId, identity.citizenId, identity.teams.join(','));
            setRun(next);
            try {
                setActivity(await getActivity(runId, identity.citizenId, identity.teams.join(',')));
            } catch {
                setActivity([]);
            }
        } catch (e) {
            if (!silent) Swal.fire('Runbook error', (e as Error).message, 'error');
        } finally {
            if (!silent) loader.hide();
        }
    };
    useEffect(() => {
        load();
    }, [runId, identity.citizenId, identity.teams.join(',')]);
    useEffect(() => {
        if (run?.status === 'draft' && ['run_owner', 'editor'].includes(run.role)) {
            history.replace(`/migration-runbook/runs/${run.id}/manage`);
        }
    }, [history, run?.id, run?.role, run?.status]);
    useEffect(() => {
        if (!identity.citizenId) return;
        const connection = new HubConnectionBuilder()
            .withUrl('/hubs/migration-runbook')
            .withAutomaticReconnect()
            .build();
        const reload = () => load(true);
        connection.on('runUpdated', reload);
        connection.on('taskUpdated', reload);
        connection.on('activityAdded', reload);
        connection.on('presenceChanged', (next: PresenceViewer[]) => setViewers(next || []));
        connection.on('cursorMoved', (cursor: Omit<RemoteCursor, 'receivedAt'>) => {
            if (!cursor?.citizenId || cursor.citizenId === identity.citizenId) return;
            setRemoteCursors(previous => ({
                ...previous,
                [cursor.citizenId]: { ...cursor, receivedAt: Date.now() }
            }));
        });
        const join = () => connection.invoke('JoinRun', runId, runbookAccessToken());
        connection.onreconnected(async () => {
            setConnected(true);
            await join();
            reload();
        });
        connection.onclose(() => {
            setConnected(false);
            setViewers([]);
        });
        connectionRef.current = connection;
        connection
            .start()
            .then(async () => {
                await join();
                setConnected(true);
            })
            .catch(() => setConnected(false));
        const poll = window.setInterval(() => {
            if (connection.state !== HubConnectionState.Connected) load(true);
        }, 30000);
        const heartbeat = window.setInterval(() => {
            if (connection.state === HubConnectionState.Connected)
                connection.invoke('Heartbeat', runId).catch(() => undefined);
        }, 10000);
        const cursorCleanup = window.setInterval(() => {
            const cutoff = Date.now() - 4000;
            setRemoteCursors(previous =>
                Object.fromEntries(
                    Object.entries(previous).filter(([, cursor]) => cursor.receivedAt >= cutoff)
                )
            );
        }, 1000);
        return () => {
            window.clearInterval(poll);
            window.clearInterval(heartbeat);
            window.clearInterval(cursorCleanup);
            connectionRef.current = null;
            setViewers([]);
            setRemoteCursors({});
            connection.stop();
        };
    }, [runId, identity.citizenId, identity.name, identity.teams.join(',')]);
    const updateFocus = (regionId: string, taskId = '') => {
        if (lastFocusRegion.current === regionId) return;
        lastFocusRegion.current = regionId;
        const connection = connectionRef.current;
        if (connection?.state === HubConnectionState.Connected)
            connection.invoke('UpdateFocus', runId, regionId, taskId).catch(() => undefined);
    };
    const moveCursor = (event: React.PointerEvent<HTMLDivElement>) => {
        const connection = connectionRef.current;
        const now = performance.now();
        if (connection?.state !== HubConnectionState.Connected || now - lastCursorSentAt.current < 100)
            return;
        const target = (event.target as Element).closest<HTMLElement>('[data-runbook-region]');
        if (!target) return;
        const rect = target.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const regionId = target.dataset.runbookRegion || 'run-root';
        updateFocus(regionId, regionId.startsWith('task:') ? regionId.substring(5) : '');
        lastCursorSentAt.current = now;
        connection
            .invoke(
                'MoveCursor',
                runId,
                regionId,
                (event.clientX - rect.left) / rect.width,
                (event.clientY - rect.top) / rect.height
            )
            .catch(() => undefined);
    };
    const canEdit = run?.role === 'run_owner' || run?.role === 'editor';
    const owner = run?.role === 'run_owner';
    const percent = run?.requiredTasks
        ? Math.round((run.completedTasks * 100) / run.requiredTasks)
        : 0;
    const section = useMemo(() => run?.sections.find(x => x.sectionType === tab), [run, tab]);
    const canActOnTab =
        (run?.status === 'in_progress' && tab === 'execution') ||
        (run?.status === 'rollback_in_progress' && tab === 'rollback');
    const mutateRun = async (action: string, confirm?: string) => {
        if (!run) return;
        if (confirm) {
            const result = await Swal.fire({
                title: confirm,
                icon: 'warning',
                showCancelButton: true
            });
            if (!result.isConfirmed) return;
        }
        try {
            loader.show();
            setRun(await changeRunStatus(run.id, action, actor(identity, run.versionNo)));
            await load(true);
        } catch (e) {
            Swal.fire('Action failed', (e as Error).message, 'error');
        } finally {
            loader.hide();
        }
    };
    const mutateTask = async (task: RunbookTask, action: string) => {
        let reason = '';
        if (['block', 'skip', 'reopen'].includes(action)) {
            const answer = await Swal.fire({
                title: `${action} task`,
                input: 'textarea',
                inputLabel: 'Reason (required)',
                showCancelButton: true,
                inputValidator: v => (!v ? 'Reason is required.' : undefined)
            });
            if (!answer.isConfirmed) return;
            reason = answer.value;
        }
        try {
            loader.show();
            await changeTaskStatus(runId, task.id, action, actor(identity, task.versionNo, reason));
            await load(true);
        } catch (e) {
            Swal.fire('Task action failed', (e as Error).message, 'error');
        } finally {
            loader.hide();
        }
    };
    const editTask = async (task: RunbookTask) => {
        const answer = await Swal.fire({
            title: 'Update task remark',
            input: 'textarea',
            inputValue: task.remark || '',
            showCancelButton: true,
            inputPlaceholder: 'Operational note (do not enter credentials)'
        });
        if (!answer.isConfirmed) return;
        try {
            await saveTask(runId, task.id, {
                ...actor(identity, task.versionNo),
                assigneeCitizenId: task.assigneeCitizenId,
                assigneeName: task.assigneeName,
                plannedMinutes: task.plannedMinutes,
                remark: answer.value || ''
            });
            await load(true);
        } catch (e) {
            Swal.fire('Update failed', (e as Error).message, 'error');
        }
    };
    if (!run) return <div />;
    return (
        <div
            ref={liveRootRef}
            data-runbook-region="run-root"
            onPointerMove={moveCursor}
            onPointerEnter={() => updateFocus('run-root')}
            className="relative min-h-screen bg-gray-50 p-3 md:p-6"
        >
            <div className="mx-auto max-w-7xl">
                <div
                    className={`mb-4 rounded-lg border bg-white p-5 ${run.blockedTasks ? 'border-red-400' : ''}`}
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <Link
                                to="/migration-runbook"
                                className="text-sm text-blue-600"
                            >
                                ← All runs
                            </Link>
                            <h1 className="mt-1 text-2xl font-bold">{run.title}</h1>
                            <p className="text-sm text-gray-500">
                                {date(run.plannedStartAt)} – {date(run.plannedEndAt)} · Owner{' '}
                                {run.ownerName || run.ownerCitizenId}
                            </p>
                        </div>
                        <div className="text-right">
                            <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${statusColor(run.status)}`}
                            >
                                {run.status.replace(/_/g, ' ')}
                            </span>
                            <div
                                className={`mt-2 text-xs ${connected ? 'text-green-600' : 'text-amber-600'}`}
                            >
                                {connected ? 'Live updates connected' : 'Polling fallback active'}
                            </div>
                            {connected && (
                                <div className="mt-2 flex items-center justify-end gap-2">
                                    <div className="flex -space-x-2">
                                        {viewers.slice(0, 5).map(viewer => (
                                            <span
                                                key={viewer.citizenId}
                                                title={`${viewer.displayName}${viewer.citizenId === identity.citizenId ? ' (you)' : ''}`}
                                                className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white shadow-sm"
                                                style={{
                                                    backgroundColor: viewerColor(viewer.citizenId)
                                                }}
                                            >
                                                {initials(viewer.displayName)}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-xs font-medium text-gray-600">
                                        {viewers.length} viewing
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    {run.blockedTasks > 0 && (
                        <div className="mt-3 rounded bg-red-50 p-3 font-semibold text-red-700">
                            Blocked: {run.blockedTasks} task(s) require attention
                        </div>
                    )}
                    <div className="mt-4 h-3 overflow-hidden rounded bg-gray-200">
                        <div
                            className="h-full bg-blue-600"
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                        {run.completedTasks}/{run.requiredTasks} required tasks · {percent}%
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {owner && (run.status === 'draft' || run.status === 'scheduled') && (
                            <Link
                                to={`/migration-runbook/runs/${run.id}/manage`}
                                className="rounded border px-3 py-2 text-sm"
                            >
                                Manage plan
                            </Link>
                        )}
                        {canEdit && run.status === 'scheduled' && (
                            <button
                                onClick={() => mutateRun('start')}
                                className="rounded bg-green-600 px-3 py-2 text-sm text-white"
                            >
                                Start run
                            </button>
                        )}
                        {canEdit &&
                            (run.status === 'in_progress' ||
                                run.status === 'rollback_in_progress') && (
                                <button
                                    onClick={() => mutateRun('complete', 'Complete this run?')}
                                    className="rounded bg-green-600 px-3 py-2 text-sm text-white"
                                >
                                    Complete
                                </button>
                            )}
                        {owner && (run.status === 'in_progress' || run.status === 'completed') && (
                            <button
                                onClick={() => mutateRun('activate-rollback', 'Activate rollback?')}
                                className="rounded bg-red-600 px-3 py-2 text-sm text-white"
                            >
                                Activate rollback
                            </button>
                        )}
                        {owner &&
                            !['completed', 'rolled_back', 'cancelled'].includes(run.status) && (
                                <button
                                    onClick={() => mutateRun('cancel', 'Cancel this run?')}
                                    className="rounded border border-red-300 px-3 py-2 text-sm text-red-700"
                                >
                                    Cancel
                                </button>
                            )}{' '}
                        {!canEdit && (
                            <span className="rounded bg-gray-100 px-3 py-2 text-sm text-gray-600">
                                Read-only viewer
                            </span>
                        )}
                    </div>
                </div>
                <div>
                    <main className="rounded-lg border bg-white">
                        <div className="flex border-b">
                            <button
                                onClick={() => setTab('execution')}
                                className={`px-5 py-3 text-sm font-semibold ${tab === 'execution' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-gray-500'}`}
                            >
                                Main Execution
                            </button>
                            <button
                                onClick={() => setTab('rollback')}
                                className={`px-5 py-3 text-sm font-semibold ${tab === 'rollback' ? 'border-b-2 border-red-600 text-red-700' : 'text-gray-500'}`}
                            >
                                Rollback
                            </button>
                        </div>
                        <div className="space-y-3 p-4">
                            {section?.steps.map(step => {
                                const done = step.tasks.filter(t =>
                                    ['completed', 'skipped'].includes(t.status)
                                ).length;
                                return (
                                    <details
                                        key={step.id}
                                        open
                                        className="rounded-lg border"
                                    >
                                        <summary
                                            data-runbook-region={`step:${step.id}`}
                                            onPointerEnter={() => updateFocus(`step:${step.id}`)}
                                            className="cursor-pointer bg-gray-50 px-4 py-3 font-semibold"
                                        >
                                            {step.sequence}. {step.title}{' '}
                                            <span className="float-right text-xs text-gray-500">
                                                {done}/{step.tasks.length}
                                            </span>
                                        </summary>
                                        <div className="divide-y">
                                            {step.tasks.map(task => (
                                                <div
                                                    key={task.id}
                                                    data-runbook-region={`task:${task.id}`}
                                                    onPointerEnter={() =>
                                                        updateFocus(`task:${task.id}`, task.id)
                                                    }
                                                    className={`relative p-4 ${task.status === 'blocked' ? 'bg-red-50' : ''}`}
                                                >
                                                    {viewers.some(
                                                        viewer =>
                                                            viewer.citizenId !==
                                                                identity.citizenId &&
                                                            viewer.focusTaskId === task.id
                                                    ) && (
                                                        <div className="mb-2 flex flex-wrap gap-1">
                                                            {viewers
                                                                .filter(
                                                                    viewer =>
                                                                        viewer.citizenId !==
                                                                            identity.citizenId &&
                                                                        viewer.focusTaskId === task.id
                                                                )
                                                                .map(viewer => (
                                                                    <span
                                                                        key={viewer.citizenId}
                                                                        className="rounded-full border bg-white px-2 py-0.5 text-[10px] font-semibold shadow-sm"
                                                                        style={{
                                                                            borderColor: viewerColor(
                                                                                viewer.citizenId
                                                                            ),
                                                                            color: viewerColor(
                                                                                viewer.citizenId
                                                                            )
                                                                        }}
                                                                    >
                                                                        {viewer.displayName} is here
                                                                    </span>
                                                                ))}
                                                        </div>
                                                    )}
                                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                                        <div>
                                                            <div className="font-medium">
                                                                {step.sequence}.{task.sequence}{' '}
                                                                {task.title}{' '}
                                                                {!task.isRequired && (
                                                                    <span className="text-xs text-gray-400">
                                                                        optional
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-gray-500">
                                                                {task.detail}
                                                            </p>
                                                        </div>
                                                        <span
                                                            className={`rounded-full px-2 py-1 text-xs ${statusColor(task.status)}`}
                                                        >
                                                            {task.status.replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                                                        <span>
                                                            Owner:{' '}
                                                            {task.assigneeName ||
                                                                task.assigneeCitizenId ||
                                                                'Unassigned'}
                                                        </span>
                                                        {task.plannedMinutes > 0 && (
                                                            <span>
                                                                Plan: {task.plannedMinutes} min
                                                            </span>
                                                        )}
                                                        {task.actualStartAt && (
                                                            <span>
                                                                Start: {date(task.actualStartAt)}
                                                            </span>
                                                        )}
                                                        {task.actualEndAt && (
                                                            <span>
                                                                Finish: {date(task.actualEndAt)}
                                                            </span>
                                                        )}
                                                        {task.actualStartAt && (
                                                            <span>
                                                                Elapsed:{' '}
                                                                {elapsed(
                                                                    task.actualStartAt,
                                                                    task.actualEndAt
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {task.blockerReason && (
                                                        <div className="mt-2 rounded bg-red-100 px-3 py-2 text-sm font-medium text-red-800">
                                                            Blocker: {task.blockerReason}
                                                        </div>
                                                    )}
                                                    {task.remark && (
                                                        <div className="mt-2 text-sm text-gray-700">
                                                            Remark: {task.remark}
                                                        </div>
                                                    )}
                                                    {canEdit && canActOnTab && (
                                                        <div className="mt-3 flex flex-wrap gap-2">
                                                            {['not_started', 'ready'].includes(
                                                                task.status
                                                            ) && (
                                                                <button
                                                                    onClick={() =>
                                                                        mutateTask(task, 'start')
                                                                    }
                                                                    className="rounded bg-blue-600 px-2 py-1 text-xs text-white"
                                                                >
                                                                    Start
                                                                </button>
                                                            )}
                                                            {task.status === 'in_progress' && (
                                                                <button
                                                                    onClick={() =>
                                                                        mutateTask(task, 'complete')
                                                                    }
                                                                    className="rounded bg-green-600 px-2 py-1 text-xs text-white"
                                                                >
                                                                    Complete
                                                                </button>
                                                            )}
                                                            {task.status === 'blocked' && (
                                                                <button
                                                                    onClick={() =>
                                                                        mutateTask(task, 'unblock')
                                                                    }
                                                                    className="rounded border px-2 py-1 text-xs"
                                                                >
                                                                    Unblock
                                                                </button>
                                                            )}
                                                            <details className="relative">
                                                                <summary className="cursor-pointer list-none rounded border px-2 py-1 text-xs">
                                                                    More actions
                                                                </summary>
                                                                <div className="absolute left-0 z-10 mt-1 flex min-w-[140px] flex-col gap-1 rounded-md border bg-white p-2 shadow-lg">
                                                                    <button
                                                                        onClick={() =>
                                                                            editTask(task)
                                                                        }
                                                                        className="rounded px-2 py-1 text-left text-xs hover:bg-gray-100"
                                                                    >
                                                                        Edit remark
                                                                    </button>
                                                                    {[
                                                                        'not_started',
                                                                        'ready',
                                                                        'in_progress'
                                                                    ].includes(task.status) && (
                                                                        <button
                                                                            onClick={() =>
                                                                                mutateTask(
                                                                                    task,
                                                                                    'block'
                                                                                )
                                                                            }
                                                                            className="rounded px-2 py-1 text-left text-xs text-red-700 hover:bg-red-50"
                                                                        >
                                                                            Block
                                                                        </button>
                                                                    )}
                                                                    {![
                                                                        'completed',
                                                                        'skipped'
                                                                    ].includes(task.status) && (
                                                                        <button
                                                                            onClick={() =>
                                                                                mutateTask(
                                                                                    task,
                                                                                    'skip'
                                                                                )
                                                                            }
                                                                            className="rounded px-2 py-1 text-left text-xs hover:bg-gray-100"
                                                                        >
                                                                            Skip
                                                                        </button>
                                                                    )}
                                                                    {owner &&
                                                                        [
                                                                            'completed',
                                                                            'skipped'
                                                                        ].includes(task.status) && (
                                                                            <button
                                                                                onClick={() =>
                                                                                    mutateTask(
                                                                                        task,
                                                                                        'reopen'
                                                                                    )
                                                                                }
                                                                                className="rounded px-2 py-1 text-left text-xs hover:bg-gray-100"
                                                                            >
                                                                                Reopen
                                                                            </button>
                                                                        )}
                                                                </div>
                                                            </details>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                );
                            })}
                            {!section?.steps.length && (
                                <div className="py-12 text-center text-gray-500">
                                    No tasks in this section.
                                </div>
                            )}
                        </div>
                    </main>
                    <details className="mt-4 rounded-lg border bg-white p-4">
                        <summary className="cursor-pointer font-semibold">
                            Activity history ({activity.length})
                        </summary>
                        <div className="mt-4 max-h-80 space-y-3 overflow-auto">
                            {activity.map(item => (
                                <div
                                    key={item.id}
                                    className="border-l-2 border-blue-200 pl-3"
                                >
                                    <div className="text-sm font-medium">{item.changeSummary}</div>
                                    <div className="text-xs text-gray-500">
                                        {item.actorName || item.actorCitizenId} ·{' '}
                                        {date(item.createdOn)}
                                    </div>
                                </div>
                            ))}
                            {!activity.length && (
                                <p className="text-sm text-gray-500">No activity yet.</p>
                            )}
                        </div>
                    </details>
                </div>
            </div>
            <CursorLayer
                rootRef={liveRootRef}
                cursors={Object.values(remoteCursors)}
            />
        </div>
    );
}
